// Lib
import { first } from 'lodash/fp';

// Utils
import * as pointLib from '../../../common/maths/geometry/point';
import * as rectLib from '../../../common/maths/geometry/rect';
import { asObject } from '../../../common/utils/immutableHelper';

// Selectors
import getGridSize from '../../utils/grid/gridSizeSelector';
import { getCurrentBoardId } from '../../reducers/currentBoardId/currentBoardIdSelector';
import { selectElementMeasurements } from '../../components/measurementsStore/elementMeasurements/elementMeasurementsSelector';
import {
    getCurrentVisibleBoardCanvasOrigin,
    getCurrentBoardVisibleDescendantIds,
} from '../../element/selectors/currentBoardSelector';

// Measurement Actions
import { createBatchAction } from '../../../common/store/reduxBulkingMiddleware';
import { measurementsSet } from '../../components/measurementsStore/elementMeasurements/elementMeasurementsActions';

// Constants
import { ELEMENT_UPDATE_TYPE } from '../../../common/elements/elementConstants';
import { DEFAULT_CANVAS_ORIGIN } from '../../../common/elements/utils/elementPositionUtils';

// Types
import { ReduxStore } from '../../types/reduxTypes';
import { Point } from '../../../common/maths/geometry/pointTypes';
import { Rect } from '../../../common/maths/geometry/rect/rectTypes';
import { ElementMeasurement } from '../../components/measurementsStore/elementMeasurements/elementMeasurementsTypes';
import { ActionObject } from '../../../common/actions/actionTypes';

interface Measurement extends Rect {
    rects: Array<Rect>;
}

/**
 * Translates a measurement from the measurements store by a given point.
 */
const shiftElementMeasurement = (elementMeasurement: Measurement, translationPx: Point): Measurement => ({
    ...elementMeasurement,
    ...rectLib.translate(translationPx, elementMeasurement),
    rects: elementMeasurement.rects.map((measurementRect) => ({
        ...measurementRect,
        ...rectLib.translate(translationPx, measurementRect),
    })),
});

/**
 * On a board canvas origin update, shift the measurements for all canvas elements
 */
export const handleElementUpdate = (action: ActionObject, store: ReduxStore, measurementsStore: ReduxStore) => {
    if (action.updateType !== ELEMENT_UPDATE_TYPE.CANVAS_ORIGIN) return;

    const { updates } = action;

    const firstUpdate = first(updates);
    const updatedBoardId = firstUpdate?.id;

    const measurementsState = measurementsStore.getState();

    const state = store.getState();
    const currentBoardId = getCurrentBoardId(state);

    // Only update measurements if the current board has its origin changed
    if (updatedBoardId !== currentBoardId) return;

    const currentCanvasOrigin: Point = getCurrentVisibleBoardCanvasOrigin(state);
    const gridSize = getGridSize(state);

    const newCanvasOrigin = firstUpdate?.changes?.canvasOrigin || DEFAULT_CANVAS_ORIGIN;

    const canvasOriginChange: Point = pointLib.difference(currentCanvasOrigin, newCanvasOrigin);
    // NOTE: Multiplying by -1 as we need to push the elements in the opposite direction
    const elementMeasurementsTranslationPx: Point = pointLib.scale(-gridSize, canvasOriginChange);

    // For all elements on the canvas, shift their measurements by the change in
    const canvasElementIds = getCurrentBoardVisibleDescendantIds(state);

    const measurementUpdates = canvasElementIds
        .map((canvasElementId: string) => {
            const elementMeasurement = selectElementMeasurements(measurementsState, { id: canvasElementId });

            if (!elementMeasurement) return null;

            const newMeasurements = shiftElementMeasurement(
                asObject(elementMeasurement),
                elementMeasurementsTranslationPx,
            ) as ElementMeasurement;

            return measurementsSet({ id: canvasElementId, measurements: newMeasurements });
        })
        .filter((update: object) => !!update);

    const batchMeasurementsUpdate = createBatchAction({ actions: measurementUpdates });

    measurementsStore.dispatch(batchMeasurementsUpdate);
};
